// FT-070 (REQ-04/REQ-05): чистый маппер billing_summary → конфиг отображения paywall.
// Без Vue/i18n-контекста — возвращает i18n-КЛЮЧИ и параметры интерполяции, чтобы
// оставаться детерминированным и юнит-тестируемым (tests/unit/helpers/billingPaywall.spec.js).
// Дату next_retry_at форматирует уже компонент перед $t.
//
// tier — единственный источник «насколько серьёзно»:
//   none   — ничего не показываем (paywall_mode none; в shadow backend всегда отдаёт none → CON-01);
//   soft   — мягкий баннер-нудж (paywall_mode soft);
//   strong — предупреждающий баннер (hard, но stage ещё не enforce → реального блока нет);
//   hard   — блок-оверлей (hard И enforced — серверный энфорсмент включён).

const REASON_GROUP = {
  trial_ending: "trial_ending",
  autopay_recommended: "autopay",
  tariff_policy: "tariff",
  repeat_block: "repeat_block", // DEC-01 FT-069: ≥ N блокировок без автоплатежа — просим автоплатёж, не «баланс исчерпан»
  abuser_auto: "abuser",
  dunning: "dunning",
  manual: "manual",
  // FT-073: MP (Managed Payments) — текст про подписку, НЕ про баланс (баланса у MP нет).
  mp_canceled: "mp_canceled",
  mp_past_due: "mp_past_due",
};

const DUNNING_TEXT_STATES = ["retrying_soft", "awaiting_rebind", "awaiting_auth", "provider_dunning", "final_warning"];

function tierFor(paywallMode, enforced) {
  if (paywallMode === "hard") return enforced ? "hard" : "strong";
  if (paywallMode === "soft") return "soft";
  return "none";
}

// Может ли клиент подключить автоплатёж: поле бэкенда billing_summary.autopay_available
// (2026-09-17); старый payload без поля — прокси по валюте (Tinkoff = RUB).
function autopayAvailable(summary) {
  if (typeof summary.autopay_available === "boolean") return summary.autopay_available;
  // Старый payload: RUB = Tinkoff; а любой шаг дуннинга сам по себе означает, что автоплатёж
  // у клиента есть (ладдер запускается только по автосписанию) — EUR Stripe в awaiting_rebind.
  if (DUNNING_TEXT_STATES.includes(summary.dunning_state)) return true;
  // Валюта неизвестна (старый/неполный payload) — не прячем: лучше лишний нудж, чем молчание.
  if (!summary.currency) return true;
  return String(summary.currency).toUpperCase() === "RUB";
}

function textKeyFor(reason, dunningState, canAutopay) {
  const group = REASON_GROUP[reason] || "generic";
  // Без доступного автоплатежа просить его нельзя — текст только про пополнение (tariff,
  // repeat_block; бэкенд такие причины без автоплатежа не отдаёт — защита фронта на старый
  // payload и прокси по валюте).
  if (!canAutopay && (group === "tariff" || group === "repeat_block")) {
    return `paywall.reason.${group}.text_no_autopay`;
  }
  if (group === "dunning") {
    const sub = DUNNING_TEXT_STATES.includes(dunningState) ? dunningState : "generic";
    if (sub === "awaiting_rebind" && !canAutopay) return "paywall.reason.dunning.awaiting_rebind.text_no_autopay";
    return `paywall.reason.dunning.${sub}.text`;
  }
  return `paywall.reason.${group}.text`;
}

// Заголовок — по причине, а не только по уровню (ревизия текстов 2026-09-17): soft-заголовок
// «Баланс заканчивается» врал для триала, нуджа автоплатежа и MP-подписки; strong на стадии
// notify ничего не ограничивает — «Требуется действие» вместо «Доступ скоро будет ограничен».
const SOFT_TITLES = {
  trial_ending: "paywall.soft.trial_title",
  autopay_recommended: "paywall.soft.autopay_title",
  mp_past_due: "paywall.soft.mp_title",
};

function titleFor(tier, reason) {
  if (tier === "soft" && SOFT_TITLES[reason]) return SOFT_TITLES[reason];
  return `paywall.${tier}.title`;
}

function ctaFor(reason, dunningState, currency, canAutopay) {
  // FT-073: MP-клиент (Managed Payments) — баланса/пополнения у него нет. Любой MP-paywall
  // (canceled → hard, past_due → soft) ведёт на /transactions к карточке подписки, где есть
  // кнопка «Оформить подписку» (canceled) или инфо о ретрае (past_due). НЕ предлагаем topup.
  if (reason === "mp_canceled" || reason === "mp_past_due") {
    return { action: "subscribe", label: "stripe_mp_lifecycle.manage_subscription", to: "/transactions" };
  }
  // Ручная блокировка администратором: текст ведёт в поддержку, кнопка пополнения её не снимет —
  // в баннере CTA нет (оверлей на enforce оставляет свой фолбэк «Пополнить»).
  if (reason === "manual") return null;
  const isRub = String(currency || "").toUpperCase() === "RUB";
  // Автоплатёж доступен (Tinkoff у RUB; поле бэкенда) + (карта слетела ИЛИ рекомендован автоплатёж
  // ИЛИ hard с гистерезисом до автоплатежа) → настройка автоплатежа (rebill-диалог на /transactions),
  // а не разовое пополнение. Недоступен — только пополнение: кнопку, которую нельзя выполнить, не
  // показываем (EUR-клиент видел «Подключить автоплатёж», а подключить не мог).
  const wantsAutopay =
    dunningState === "awaiting_rebind" || ["autopay_recommended", "tariff_policy", "repeat_block"].includes(reason);
  if (canAutopay && wantsAutopay) {
    return { action: "rebind", label: "paywall.cta.rebind", to: "/transactions" };
  }
  // FT-071: не-RUB (EUR Stripe-автоплатёж) dunning.
  //   awaiting_auth   → платёж требует подтверждения/3DS (invoice.payment_action_required) —
  //                     клиент должен подтвердить оплату (Stripe hosted-страница из /transactions);
  //   awaiting_rebind → карта слетела, нужно переподвязать (Stripe hosted Checkout из TopupDialog);
  //   provider_dunning → soft-отказ, Stripe сам ретраит (Smart Retries) — реального действия от
  //                     клиента не требуется, мягко предлагаем пополнить баланс (см. fallthrough).
  if (!isRub && dunningState === "awaiting_auth") {
    return { action: "confirm", label: "paywall.cta.confirm", to: "/transactions" };
  }
  // Иначе (вкл. не-RUB прочие состояния) — пополнить баланс.
  return { action: "topup", label: "paywall.cta.topup", to: "/transactions" };
}

// summary — плоский объект от store-геттера getPaywall:
// { paywall_mode, paywall_reason, enforced, dunning_state, next_retry_at, currency }.
// billing_summary с бэкенда вложен (dunning_summary.{state,next_retry_at}) — геттер уплощает.
export function paywallView(summary = {}) {
  const paywallMode = summary.paywall_mode || "none";
  const enforced = summary.enforced === true;
  const reason = summary.paywall_reason || null;
  const dunningState = summary.dunning_state || "none";
  const nextRetryAt = summary.next_retry_at || null;
  const currency = summary.currency;
  const canAutopay = autopayAvailable(summary);

  let tier = tierFor(paywallMode, enforced);
  // Нудж «подключите автоплатёж» тому, кому автоплатёж недоступен, — шум без действия:
  // не показываем вовсе (бэкенд такой soft не отдаёт; защита на старый payload / прокси).
  if (tier === "soft" && reason === "autopay_recommended" && !canAutopay) tier = "none";

  if (tier === "none") {
    return { tier, title: null, text: null, textParams: {}, cta: null };
  }

  return {
    tier,
    title: titleFor(tier, reason),
    text: textKeyFor(reason, dunningState, canAutopay),
    textParams: nextRetryAt ? { next_retry_at: nextRetryAt } : {},
    cta: ctaFor(reason, dunningState, currency, canAutopay),
  };
}

// Закрытие баннера (soft/strong) с памятью в localStorage (USER 2026-09-15). Ключ — на тенанта
// (общий браузер у двух компаний не должен прятать чужой баннер), значение — подпись состояния:
// tier|reason|dunning_state. Баннер скрыт, пока подпись совпадает; любое изменение состояния
// (soft → strong, следующий шаг ладдера, другая причина) показывает его снова. Блок-оверлей
// (hard) закрыть нельзя — его здесь нет. localStorage может быть недоступен (приватный режим,
// Capacitor webview) — все обращения в try/catch, отказ = «не закрывали».
export const BANNER_DISMISS_KEY_PREFIX = "billingPaywall.dismissed";

export function bannerDismissKey(mainCompanyId) {
  return `${BANNER_DISMISS_KEY_PREFIX}:${mainCompanyId == null ? "anon" : mainCompanyId}`;
}

export function bannerSignature(summary = {}) {
  const view = paywallView(summary);
  if (view.tier === "none" || view.tier === "hard") return null;
  // Знак баланса — часть подписи: закрыл нудж при малом запасе → уход в минус показывает снова.
  const sign = summary.balance_negative ? "minus" : "plus";
  return `${view.tier}|${summary.paywall_reason || ""}|${summary.dunning_state || "none"}|${sign}`;
}

export function readDismissedSignature(storage, key) {
  try {
    return storage ? storage.getItem(key) : null;
  } catch (e) {
    return null;
  }
}

export function rememberDismissedSignature(storage, key, signature) {
  try {
    if (storage && signature) storage.setItem(key, signature);
  } catch (e) {
    // приватный режим / квота — просто не запоминаем
  }
}
